/**
 * Menu mobile.
 *
 * Requisiti di accessibilita' non negoziabili:
 * - il pulsante dichiara il proprio stato con aria-expanded
 * - Escape chiude
 * - il focus resta intrappolato nel pannello mentre e' aperto, altrimenti chi
 *   naviga da tastiera esce dal menu senza accorgersene e continua a tabulare
 *   in una pagina che non vede
 * - alla chiusura il focus torna al pulsante che aveva aperto
 */
class NavigationDrawer extends HTMLElement {
  connectedCallback() {
    this.toggle = this.querySelector('[data-drawer-toggle]');
    this.panel = this.querySelector('[data-drawer-panel]');
    this.closeButton = this.querySelector('[data-drawer-close]');
    this.overlay = this.querySelector('[data-drawer-overlay]');

    if (!this.toggle || !this.panel) return;

    this.open = this.open.bind(this);
    this.close = this.close.bind(this);
    this.onKeydown = this.onKeydown.bind(this);

    this.toggle.addEventListener('click', this.open);
    this.closeButton?.addEventListener('click', this.close);
    this.overlay?.addEventListener('click', this.close);
    this.addEventListener('keydown', this.onKeydown);
  }

  disconnectedCallback() {
    document.documentElement.classList.remove('drawer-open');
  }

  open() {
    this.classList.add('is-open');
    this.toggle.setAttribute('aria-expanded', 'true');
    if (this.overlay) this.overlay.hidden = false;
    // Blocca lo scorrimento del documento sotto il pannello.
    document.documentElement.classList.add('drawer-open');

    // Il focus va spostato DOPO che il browser ha applicato lo stile: su un
    // elemento ancora invisibile focus() non ha effetto, e chi apre da
    // tastiera si ritroverebbe a tabulare dall'inizio del documento.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => this.focusables()[0]?.focus());
    });
  }

  close() {
    this.classList.remove('is-open');
    this.toggle.setAttribute('aria-expanded', 'false');
    if (this.overlay) this.overlay.hidden = true;
    document.documentElement.classList.remove('drawer-open');
    this.toggle.focus();
  }

  get isOpen() {
    return this.classList.contains('is-open');
  }

  /**
   * Elementi raggiungibili da tastiera dentro il pannello.
   * offsetParent esclude quelli nascosti: senza il filtro, la trappola del
   * focus manderebbe il focus su elementi invisibili.
   */
  focusables() {
    return Array.from(
      this.panel.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    ).filter((el) => el.offsetParent !== null);
  }

  onKeydown(event) {
    if (!this.isOpen) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      return;
    }

    if (event.key !== 'Tab') return;

    const items = this.focusables();
    if (items.length === 0) return;

    const first = items[0];
    const last = items[items.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }
}

if (!customElements.get('navigation-drawer')) {
  customElements.define('navigation-drawer', NavigationDrawer);
}
